import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Database, Download, Upload, RefreshCw, AlertTriangle,
  CheckCircle2, AlertCircle, Clock, Server,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ENV_PILL = {
  production: 'bg-rose-100 text-rose-800 border-rose-300',
  preview: 'bg-amber-100 text-amber-800 border-amber-300',
  staging: 'bg-blue-100 text-blue-800 border-blue-300',
  development: 'bg-gray-100 text-gray-700 border-gray-300',
  dev: 'bg-gray-100 text-gray-700 border-gray-300',
};

const SeoMigrationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(''); // 'export' | 'import' | ''
  const [info, setInfo] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [importMode, setImportMode] = useState('replace');
  const [backup, setBackup] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [lastImportSummary, setLastImportSummary] = useState(null);
  const fileInputRef = useRef(null);

  const axiosCfg = { withCredentials: true };

  const fetchInfo = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${BACKEND_URL}/api/admin/seo-migration/info`, axiosCfg);
      setInfo(r.data);
    } catch (e) {
      console.error('Failed to fetch SEO migration info', e);
      setMessage({ type: 'error', text: 'Failed to load SEO migration info.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const handleExport = async () => {
    setBusy('export');
    setMessage({ type: '', text: '' });
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/seo-migration/export`, {
        ...axiosCfg,
        responseType: 'blob',
      });
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `seo_bundle_${Date.now()}.tar.gz`;
      const blob = new Blob([r.data], { type: 'application/gzip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: `Exported ${fname} (${(blob.size / 1024).toFixed(1)} KB)` });
      fetchInfo();
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Export failed.' });
    } finally {
      setBusy('');
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.endsWith('.tar.gz') && !f.name.endsWith('.tgz')) {
      setMessage({ type: 'error', text: 'File must be a .tar.gz / .tgz bundle.' });
      e.target.value = '';
      return;
    }
    setPendingFile(f);
    setConfirmOpen(true);
  };

  const handleImport = async () => {
    if (!pendingFile) return;
    setBusy('import');
    setMessage({ type: '', text: '' });
    setConfirmOpen(false);
    const form = new FormData();
    form.append('file', pendingFile);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/seo-migration/import?mode=${importMode}&backup=${backup}`,
        form,
        { ...axiosCfg, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (r.data?.success) {
        setLastImportSummary(r.data);
        const totals = Object.values(r.data.collections || {}).reduce(
          (acc, v) => ({
            inserted: acc.inserted + (v.inserted || 0),
            updated: acc.updated + (v.updated || 0),
            skipped: acc.skipped + (v.skipped || 0),
          }),
          { inserted: 0, updated: 0, skipped: 0 },
        );
        setMessage({
          type: 'success',
          text: `Imported successfully — inserted ${totals.inserted}, updated ${totals.updated}, skipped ${totals.skipped}.`,
        });
        fetchInfo();
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Import failed.' });
    } finally {
      setBusy('');
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <section
        data-testid="seo-migration-card"
        className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm"
      >
        <div className="animate-pulse h-40 bg-gray-100 rounded-lg" />
      </section>
    );
  }

  const env = info?.environment || 'unknown';
  const envPill = ENV_PILL[env] || 'bg-gray-100 text-gray-700 border-gray-300';
  const total = info?.total_documents || 0;
  const counts = info?.collections || {};
  const recent = info?.recent_operations || [];

  return (
    <section
      data-testid="seo-migration-card"
      className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
          <Database className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-gray-900">SEO Data Migration</h2>
            <span
              data-testid="seo-migration-env"
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border rounded ${envPill}`}
            >
              <Server className="w-3 h-3" />
              {env}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-gray-100 text-gray-700 rounded">
              {total.toLocaleString()} docs across {Object.keys(counts).length} collections
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Move enriched SEO pages, content blocks, routes, airports, airlines, cities,
            countries, and visa data between environments. Bookings, agents, and users are
            never touched.
          </p>
        </div>
        <button
          type="button"
          data-testid="seo-migration-refresh"
          onClick={fetchInfo}
          className="p-2 rounded-lg hover:bg-gray-100"
          title="Refresh counts"
        >
          <RefreshCw className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Collection counts */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-6">
        {Object.entries(counts).map(([col, n]) => (
          <div
            key={col}
            data-testid={`seo-migration-count-${col}`}
            className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-xs"
          >
            <span className="font-mono text-gray-700 truncate">{col}</span>
            <span className={`font-bold ${n ? 'text-gray-900' : 'text-gray-400'}`}>
              {n.toLocaleString()}
            </span>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Export */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-2">
            <Download className="w-4 h-4 text-emerald-600" /> Export bundle
          </h3>
          <p className="text-xs text-gray-600 mb-3">
            Downloads a single <code>.tar.gz</code> with every SEO collection as JSONL +
            a manifest (env, counts, SHA-256). Safe to run anytime — read-only.
          </p>
          <button
            type="button"
            data-testid="seo-migration-export-btn"
            onClick={handleExport}
            disabled={busy === 'export' || total === 0}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {busy === 'export' ? 'Building bundle…' : 'Download SEO Bundle'}
          </button>
        </div>

        {/* Import */}
        <div className="border border-rose-200 rounded-lg p-4 bg-rose-50/30">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-2">
            <Upload className="w-4 h-4 text-rose-600" /> Import bundle
          </h3>
          <p className="text-xs text-gray-600 mb-3">
            Uploads a previously-exported bundle and writes it into <strong>{env}</strong>.
            Read the mode setting carefully — replace is destructive.
          </p>
          <div className="space-y-2 mb-3">
            <label className="flex items-start gap-2 text-xs">
              <input
                type="radio"
                data-testid="seo-migration-mode-replace"
                name="seo-migration-mode"
                value="replace"
                checked={importMode === 'replace'}
                onChange={() => setImportMode('replace')}
                className="mt-0.5"
              />
              <div>
                <span className="font-semibold text-gray-900">Replace</span>
                <span className="text-gray-600"> — drop each collection, then bulk-insert (full override)</span>
              </div>
            </label>
            <label className="flex items-start gap-2 text-xs">
              <input
                type="radio"
                data-testid="seo-migration-mode-upsert"
                name="seo-migration-mode"
                value="upsert"
                checked={importMode === 'upsert'}
                onChange={() => setImportMode('upsert')}
                className="mt-0.5"
              />
              <div>
                <span className="font-semibold text-gray-900">Upsert</span>
                <span className="text-gray-600"> — overwrite by stable key, keep destination-only docs</span>
              </div>
            </label>
            <label className="flex items-center gap-2 text-xs pt-1">
              <input
                type="checkbox"
                data-testid="seo-migration-backup"
                checked={backup}
                onChange={(e) => setBackup(e.target.checked)}
              />
              <span className="text-gray-700">Backup current data to <code>&lt;collection&gt;_backup_&lt;timestamp&gt;</code> before import</span>
            </label>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".tar.gz,.tgz,application/gzip"
            onChange={onPickFile}
            disabled={busy === 'import'}
            data-testid="seo-migration-file"
            className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-rose-600 file:text-white hover:file:bg-rose-700 disabled:opacity-50"
          />
        </div>
      </div>

      {message.text && (
        <div
          data-testid="seo-migration-message"
          className={`mt-4 p-3 rounded-lg flex items-start gap-2 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Last import per-collection summary */}
      {lastImportSummary && (
        <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-700">
            Last import — mode: {lastImportSummary.mode}, backup: {String(lastImportSummary.backup)}
          </div>
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="text-left text-gray-600">
                <th className="px-3 py-1.5">Collection</th>
                <th className="px-3 py-1.5 text-right">Incoming</th>
                <th className="px-3 py-1.5 text-right">Inserted</th>
                <th className="px-3 py-1.5 text-right">Updated</th>
                <th className="px-3 py-1.5 text-right">Skipped</th>
                <th className="px-3 py-1.5">Backup</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(lastImportSummary.collections || {}).map(([col, s]) => (
                <tr key={col} className="border-t border-gray-100">
                  <td className="px-3 py-1 font-mono text-gray-800">{col}</td>
                  <td className="px-3 py-1 text-right">{s.incoming}</td>
                  <td className="px-3 py-1 text-right text-emerald-700 font-semibold">{s.inserted}</td>
                  <td className="px-3 py-1 text-right text-blue-700 font-semibold">{s.updated}</td>
                  <td className="px-3 py-1 text-right text-gray-500">{s.skipped}</td>
                  <td className="px-3 py-1 font-mono text-gray-600 text-[10px] truncate">
                    {s.backup_to || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent ops audit */}
      {recent.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Recent migrations
          </h4>
          <ul className="space-y-1">
            {recent.map((op) => (
              <li key={op.id} className="text-xs text-gray-600 flex items-center gap-2">
                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  op.kind === 'import' ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'
                }`}>
                  {op.kind}
                </span>
                <span className="font-mono">{op.timestamp}</span>
                <span className="text-gray-500">· {op.admin_id?.slice(0, 8) || 'admin'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Confirmation modal */}
      {confirmOpen && pendingFile && (
        <div
          data-testid="seo-migration-confirm-modal"
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => busy !== 'import' && setConfirmOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Confirm SEO data import</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Importing into <strong>{env}</strong> environment.
                </p>
              </div>
            </div>
            <ul className="text-sm text-gray-700 space-y-1 mb-4 pl-4 list-disc">
              <li>File: <code className="text-xs">{pendingFile.name}</code> ({(pendingFile.size / 1024).toFixed(1)} KB)</li>
              <li>Mode: <strong>{importMode}</strong> {importMode === 'replace' ? '— drops each collection then inserts' : '— overwrite by id, keep extras'}</li>
              <li>Backup: <strong>{backup ? 'enabled' : 'disabled'}</strong> {backup ? '(safe rollback available)' : '(no rollback!)'}</li>
              <li>Current total docs in destination: <strong>{total.toLocaleString()}</strong></li>
            </ul>
            {env === 'production' && (
              <div className="p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-800 mb-4">
                <strong>⚠ PRODUCTION:</strong> This action is live-traffic-visible. New SEO pages will be served within seconds.
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                data-testid="seo-migration-confirm-cancel"
                onClick={() => { setConfirmOpen(false); setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                disabled={busy === 'import'}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="seo-migration-confirm-import"
                onClick={handleImport}
                disabled={busy === 'import'}
                className="px-4 py-2 text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-50"
              >
                {busy === 'import' ? 'Importing…' : `Yes, ${importMode}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SeoMigrationSettings;
